<template>
  <ScaleLoader v-if="loading" />
  <DxDataGrid
    v-else
    :ref="dataGridRef"
    :allow-column-reordering="true"
    :allow-column-resizing="true"
    :column-auto-width="true"
    :column-hiding-enabled="true"
    :data-source="store"
    :hover-state-enabled="true"
    :remote-operations="remoteOperations"
    :row-alternation-enabled="true"
    :show-borders="true"
    :show-column-lines="true"
    :show-row-lines="false"
  >
    <DxHeaderFilter :visible="true" />
    <DxPaging
      :page-index="0"
      :page-size="25"
    /> <!-- Shows the second page -->
    <DxPager
      :allowed-page-sizes="[10, 20, 25]"
      :show-info="true"
      :show-navigation-buttons="true"
      :show-page-size-selector="true"
    />
    <DxFilterRow :visible="true" />
    <DxColumnFixing :enabled="true" />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.date')"
      cell-template="date-cell-template"
    />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.imei')"
      data-field="imei"
    />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.distance.distance')"
      cell-template="distance-cell-template"
    />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.odometer.odometer')"
      cell-template="odometer-cell-template"
    />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.time.time')"
      cell-template="time-cell-template"
    />
    <DxColumn
      :caption="$t('components.history.dailySummary.dataGrids.fuel.fuel')"
      cell-template="fuel-cell-template"
    />
    <template #date-cell-template="{ data }">
      <p>{{ parseDate(data.data.data_date) }}</p>
    </template>
    <template #distance-cell-template="{ data }">
      <p>{{ $t('components.history.dailySummary.dataGrids.distance.map') }}: {{ data.data.distance }} [km]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.distance.can') }}: {{ data.data.can_distance }} [km]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.distance.placeStart') }}: {{ data.data.place_start }}</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.distance.placeStop') }}: {{ data.data.place_stop }}</p>
    </template>
    <template #odometer-cell-template="{ data }">
      <p>{{ $t('components.history.dailySummary.dataGrids.odometer.start') }}: {{ data.data.odometer_start }} [km]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.odometer.stop') }}: {{ data.data.odometer_stop }} [km]</p>
    </template>
    <template #time-cell-template="{ data }">
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.start') }}: {{
          getHumanDateTime(data.data.time_start)
        }}
      </p>
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.stop') }}: {{
          getHumanDateTime(data.data.time_stop)
        }}
      </p>
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.ignition') }}:
        {{ secondsToHoursMinutes(data.data.ignition_time) }}
      </p>
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.drive') }}:
        {{ secondsToHoursMinutes(data.data.drive_time) }}
      </p>
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.work') }}: {{
          secondsToHoursMinutes(data.data.work_time)
        }}
      </p>
      <p>
        {{ $t('components.history.dailySummary.dataGrids.time.rest') }}: {{
          secondsToHoursMinutes(data.data.rest_time)
        }}
      </p>
    </template>
    <template #fuel-cell-template="{ data }">
      <p>{{ $t('components.history.dailySummary.dataGrids.fuel.fuelStart') }}: {{ data.data.fuel_start }} [L]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.fuel.fuelStop') }}: {{ data.data.fuel_stop }} [L]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.fuel.fuelUsed') }}: {{ data.data.fuel_used }} [L]</p>
      <p>{{ $t('components.history.dailySummary.dataGrids.fuel.fuelRate') }}: {{ data.data.fuel_rate }} [L/100]</p>
    </template>
  </DxDataGrid>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { getHistoryDailySummary } from '@/crud/getters'
import moment from 'moment'
import { getHumanDateTime, secondsToHoursMinutes } from '@/utils/tools'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'

export default defineComponent({
  name: 'DailySummary',
  components: {
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object
  },
  data(): {
    store: Array<any>,
    dataGridRef: string,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    loading: boolean
  } {
    return {
      store: [],
      dataGridRef: 'daily_summary',
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: true,
      loading: true
    }
  },
  methods: {
    parseDate(date: string) {
      const strToDate = new Date(date)
      return moment(strToDate).format('DD/MM/YYYY')
    },
    getHumanDateTime: getHumanDateTime,
    secondsToHoursMinutes: secondsToHoursMinutes
  },
  mounted() {
    const self: any = this
    if (self.imei && self.dateFrom && self.dateTo) {
      getHistoryDailySummary((this.imei as bigint), self.dateFrom, self.dateTo)
          .then((response) => {
            this.store = response.data
            this.loading = false
          })
          .catch(() => {
            self.$swal.fire({
              icon: 'warning',
              title: 'Oops...',
              text: this.$t('components.error')
            })

            this.loading = false
          })
    }
  }
})
</script>

<style lang="scss" scoped>
p {
  margin: 0;
}
</style>